import React from "react";
import { Link } from "react-router-dom";
import { LogIn, LogOut, Settings, UserRound } from "lucide-react";
import { toast } from "sonner";
import { useAccount } from "@/lib/AccountContext";
import { accountDisplayName } from "@/lib/accountProfile";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function AccountNav() {
  const { user, loading, logout } = useAccount();
  const { t } = useLang();

  if (loading) {
    return <div className="h-8 w-8 animate-pulse rounded-full bg-secondary" aria-label={t("account.loading")} />;
  }

  if (!user) {
    return (
      <Button asChild size="sm" variant="ghost" className="h-9 min-w-9 rounded-full px-2 sm:px-3" data-testid="account-nav-login">
        <Link to="/login">
          <LogIn className="h-4 w-4" />
          <span className="hidden sm:inline">{t("account.signin")}</span>
          <span className="sr-only sm:hidden">{t("account.signin")}</span>
        </Link>
      </Button>
    );
  }

  const greeting = t("account.hello", { name: accountDisplayName(user) });

  const handleLogout = async () => {
    await logout();
    toast.success(t("account.signed_out"));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="h-9 max-w-[11rem] rounded-full px-2 sm:px-3"
          data-testid="account-nav-menu"
          aria-label={t("account.menu")}
        >
          <UserRound className="h-4 w-4" />
          <span className="hidden max-w-[8rem] truncate sm:inline" data-testid="account-nav-greeting">{greeting}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="font-normal">
          <div className="truncate text-teal-900">{greeting}</div>
          <div className="truncate text-xs text-muted-foreground">{user.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/account/results" data-testid="account-nav-results">
            <UserRound className="h-4 w-4" /> {t("account.my_results")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/account/settings" data-testid="account-nav-settings">
            <Settings className="h-4 w-4" /> {t("account.settings.title")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={handleLogout} data-testid="account-nav-logout">
          <LogOut className="h-4 w-4" /> {t("account.signout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
